import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import { SidebarConfigItem } from "@/components/sidebar/sidebar-service.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useBlogMyChannelsUnreadCount } from "@/queries/blog.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { Box, FontAwesomeIcon, SidebarPanelItem, Stack, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

export const SidebarTeamBaseMenu = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data: employee } = useEmployeeMe();
  const {
    canIView,
    canIEdit,
    isFeatureEnabled,
    canCurrentEmployeeViewField,
    canCurrentEmployeeEditField,
  } = usePermissions();
  const canViewEmployees = canIEdit(ApiPermission.see_employee_directory);
  const canSeeDepartmentsChart = canCurrentEmployeeEditField(
    EmployeeFieldPermission.see_departments_structure
  );
  const { data: myChannelsUnreadCount } = useBlogMyChannelsUnreadCount(
    isFeatureEnabled(ApiFeatureType.blog)
  );

  const canSeeTeams = canIView(ApiPermission.manage_teams);

  const profileItem = useMemo(() => {
    const employeeProfilePath = `/employees/${employee?.id}`;
    return {
      title: t("core:my_profile"),
      to: employeeProfilePath,
      isActive: pathname === employeeProfilePath,
    } as SidebarConfigItem;
  }, [pathname, employee?.id]);

  return (
    <Stack className={"px-4"} gap={"lg"}>
      <Stack gap={"xs"}>
        <SidebarPanelItem
          to={profileItem.to}
          isActive={profileItem.isActive}
          title={profileItem.title}
          icon={employee && <EmployeeAvatar variant={"plain"} size="sm" employee={employee} />}
        />
      </Stack>
      <Stack gap={"xs"}>
        {canViewEmployees && (
          <SidebarPanelItem
            to={"/employees"}
            title={t("core:employees")}
            isActive={pathname == "/employees"}
            icon={<FontAwesomeIcon icon="fa-light fa-address-card" />}
          />
        )}
        {canCurrentEmployeeEditField(EmployeeFieldPermission.see_organization_structure) && (
          <SidebarPanelItem
            to={"/employees/chart"}
            title={t("core:org_chart_employees")}
            isActive={pathname == "/employees/chart"}
            icon={<FontAwesomeIcon icon="fa-light fa-sitemap" />}
          />
        )}

        {canSeeDepartmentsChart && (
          <SidebarPanelItem
            to={"/departments/chart"}
            title={t("core:org_chart_departments")}
            isActive={pathname == "/departments/chart"}
            icon={<FontAwesomeIcon icon="fa-light fa-chart-pyramid" />}
          />
        )}

        {canSeeTeams && (
          <SidebarPanelItem
            to={"/employees/teams"}
            title={t("core:teams")}
            isActive={pathname.startsWith("/employees/teams")}
            icon={<FontAwesomeIcon icon="fa-light fa-users-between-lines" />}
          />
        )}
      </Stack>

      {isFeatureEnabled(ApiFeatureType.blog) && (
        <Stack gap={"xs"}>
          <Box className={"px-2"}>
            <Title header={t("blog:news")} size={"xs"} />
          </Box>
          <SidebarPanelItem
            to={"/news"}
            title={t("blog:news_and_updates")}
            isActive={/\/news/gi.test(pathname)}
            subtitleSlot={
              myChannelsUnreadCount?.unread_post_count ? (
                <NotificationCount count={myChannelsUnreadCount?.unread_post_count} size={"sm"} />
              ) : undefined
            }
          />
        </Stack>
      )}

      {isFeatureEnabled(ApiFeatureType.time_off) &&
        ((canViewEmployees &&
          canCurrentEmployeeEditField(EmployeeFieldPermission.access_to_leave_timeline)) ||
          canIEdit(ApiPermission.reporting_leave_balance) ||
          canIEdit(ApiPermission.reporting_leave_used)) && (
          <Stack gap={"xs"}>
            <Box className={"px-2"}>
              <Title header={t("time_off:time_off")} size={"xs"} />
            </Box>

            {canViewEmployees &&
              canCurrentEmployeeEditField(EmployeeFieldPermission.access_to_leave_timeline) && (
                <SidebarPanelItem
                  to={"/time-off/calendar"}
                  title={t("common:calendar")}
                  isActive={pathname == "/time-off/calendar"}
                />
              )}

            {canIEdit(ApiPermission.reporting_leave_balance) && (
              <SidebarPanelItem
                to={"/time-off/balances-report"}
                title={t("common:balances")}
                isActive={pathname.startsWith("/time-off/balances-report")}
              />
            )}

            {canIEdit(ApiPermission.reporting_leave_used) && (
              <SidebarPanelItem
                to={"/time-off/history-report"}
                title={t("common:history")}
                isActive={pathname == "/time-off/history-report"}
              />
            )}
          </Stack>
        )}
      {isFeatureEnabled(ApiFeatureType.clocking) &&
        canCurrentEmployeeViewField(EmployeeFieldPermission.clocking_entries) && (
          <Stack gap={"xs"}>
            <Box className={"px-2"}>
              <Title header={t("clocking:time_tracking")} size={"xs"} />
            </Box>
            <SidebarPanelItem
              to={"/employees/timesheets"}
              title={t("clocking:timesheets")}
              isActive={pathname.startsWith("/employees/timesheets")}
            />
          </Stack>
        )}
    </Stack>
  );
};
